import React, { useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/styles";
import { Button, Container, Grid, Typography } from "@material-ui/core";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { headRewrite } from "../function/headRewrite";

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#eeeeee",
    color: "#111",
    minHeight: "100vh",
    margin: 0,
    padding: 0,
    fontFamily:
      '"Helvetica Neue", Arial, "Noto Sans JP", "Yu Gothic", sans-serif',
  },
  pageFrame: {
    background: "#eeeeee",
  },
  previewSwitch: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    background: "rgba(238, 238, 238, 0.96)",
    backdropFilter: "blur(10px)",
    borderBottom: "1px solid #c8c8c8",
  },
  previewSwitchInner: {
    minHeight: 74,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(3),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5, 0),
    },
  },
  previewTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(0.5),
  },
  previewSubTitle: {
    fontSize: 14,
    color: "#333",
  },
  previewButtons: {
    display: "flex",
    gap: theme.spacing(1.2),
    flexWrap: "wrap",
  },
  previewButton: {
    borderRadius: 0,
    border: "1px solid #111",
    boxShadow: "none",
    padding: theme.spacing(1.1, 2.2),
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: "0.04em",
    "&:hover": {
      boxShadow: "none",
    },
  },
  previewButtonActive: {
    background: "#111",
    color: "#eeeeee",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
    },
  },
  previewButtonInactive: {
    background: "transparent",
    color: "#111",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
    },
  },
  hero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(6, 0, 7),
    },
  },
  section: {
    padding: theme.spacing(10, 0),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 0),
    },
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    fontSize: 12,
    letterSpacing: "0.12em",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  labelLine: {
    flex: 1,
    height: 1,
    background: "#111",
    opacity: 0.7,
  },
  heroTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(30px, 4.7vw, 54px)",
    lineHeight: 1.22,
    letterSpacing: "-0.07em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  heading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(28px, 4vw, 48px)",
    lineHeight: 1.45,
    letterSpacing: "-0.05em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  subHeading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(22px, 2.6vw, 32px)",
    lineHeight: 1.5,
    letterSpacing: "-0.04em",
    fontWeight: 500,
    marginBottom: theme.spacing(2.5),
  },
  heroBody: {
    maxWidth: 650,
    fontSize: 15,
    lineHeight: 2.05,
    color: "#222",
    marginBottom: theme.spacing(4),
    whiteSpace: "pre-line",
  },
  body: {
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    whiteSpace: "pre-line",
  },
  noteBody: {
    fontSize: 14,
    lineHeight: 2,
    color: "#333",
    whiteSpace: "pre-line",
  },
  buttonRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(4),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      textAlign: "center",
    },
  },
  primaryButton: {
    background: "#111",
    color: "#eeeeee",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#eeeeee",
      color: "#111",
      boxShadow: "none",
    },
  },
  textLink: {
    color: "#111",
    textDecoration: "none",
    borderBottom: "1px solid #111",
    paddingBottom: 4,
    fontSize: 14,
    fontWeight: 700,
  },
  keywordLine: {
    borderTop: "1px solid #111",
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    fontSize: 13,
    color: "#333",
  },
  heroVisual: {
    position: "relative",
    minHeight: 520,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      minHeight: 360,
      marginTop: theme.spacing(6),
    },
  },
  heroCircle: {
    position: "absolute",
    right: -40,
    bottom: 20,
    width: 260,
    height: 260,
    border: "1px solid #111",
    borderRadius: "50%",
    opacity: 0.65,
    [theme.breakpoints.down("sm")]: {
      width: 180,
      height: 180,
      right: -60,
    },
  },
  heroSquare: {
    position: "absolute",
    left: 20,
    top: 48,
    width: 180,
    height: 180,
    border: "1px solid #111",
    opacity: 0.55,
    [theme.breakpoints.down("sm")]: {
      width: 120,
      height: 120,
      left: 0,
    },
  },
  heroNumber: {
    position: "absolute",
    top: 20,
    right: 0,
    writingMode: "vertical-rl",
    fontSize: 13,
    letterSpacing: "0.12em",
    fontWeight: 700,
  },
  imageMock: {
    position: "relative",
    width: "72%",
    maxWidth: 390,
    minHeight: 420,
    border: "1px solid #111",
    background: "rgba(255, 255, 255, 0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    textAlign: "center",
    transform: "rotate(2deg)",
    [theme.breakpoints.down("sm")]: {
      width: "82%",
      minHeight: 290,
      maxWidth: 300,
    },
  },
  imageMockImage: {
    width: "100%",
    height: "100%",
    minHeight: 450,
    objectFit: "cover",
    display: "block",
    [theme.breakpoints.down("sm")]: {
      minHeight: 250,
    },
  },
  imageMockText: {
    fontSize: 12,
    lineHeight: 2,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#333",
  },
  thesisCard: {
    border: "1px solid #111",
    padding: theme.spacing(4),
    background: "transparent",
    minHeight: 280,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      minHeight: "auto",
    },
  },
  thesisLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  thesisText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(21px, 2.4vw, 30px)",
    lineHeight: 1.7,
    letterSpacing: "-0.03em",
    fontWeight: 500,
  },
  card: {
    border: "1px solid #111",
    minHeight: 300,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
    [theme.breakpoints.down("sm")]: {
      minHeight: 240,
    },
  },
  cardNumber: {
    fontSize: 24,
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "0.02em",
    marginBottom: theme.spacing(2),
  },
  smallRule: {
    width: 32,
    height: 1,
    background: "#111",
    marginBottom: theme.spacing(3),
  },
  archiveList: {
    borderTop: "1px solid #111",
  },
  archiveListItem: {
    display: "grid",
    gridTemplateColumns: "72px 260px 1fr",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 0),
    borderBottom: "1px solid #c8c8c8",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1.5),
    },
  },
  archiveListIndex: {
    fontSize: 14,
    fontWeight: 700,
    color: "#555",
  },
  archiveListTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.6,
  },
  quoteBlock: {
    border: "1px solid #111",
    padding: theme.spacing(5),
    position: "relative",
    background: "#111",
    color: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4, 3),
    },
  },
  quoteMark: {
    fontFamily: '"Times New Roman", serif',
    fontSize: 72,
    lineHeight: 0.8,
    opacity: 0.75,
    marginBottom: theme.spacing(2),
  },
  quoteText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(22px, 3vw, 36px)",
    lineHeight: 1.7,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  experienceBox: {
    border: "1px solid #111",
    padding: theme.spacing(4),
    background: "transparent",
  },
  experienceItem: {
    borderTop: "1px solid #c8c8c8",
    padding: theme.spacing(2.2, 0),
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    fontSize: 15,
    lineHeight: 1.8,
    "&:first-child": {
      borderTop: "none",
      paddingTop: 0,
    },
  },
  experienceArrow: {
    fontWeight: 700,
  },
  imageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  imagePanel: {
    minHeight: 360,
    border: "1px solid #111",
    background: "rgba(255, 255, 255, 0.16)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: theme.spacing(2.5),
  },
  imagePanelLink: {
    color: "inherit",
    textDecoration: "none",
    transition: "background 180ms ease, transform 180ms ease",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.36)",
      transform: "translateY(-2px)",
    },
  },
  imagePanelImage: {
    width: "100%",
    aspectRatio: "1 / 1",
    objectFit: "cover",
    border: "1px solid #111",
    marginBottom: theme.spacing(2.5),
    display: "block",
    background: "#eeeeee",
  },
  imagePanelLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  imagePanelTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 24,
    lineHeight: 1.45,
    letterSpacing: "-0.04em",
    marginBottom: theme.spacing(2),
  },
  imagePanelText: {
    fontSize: 14,
    lineHeight: 2,
    color: "#333",
  },
  audienceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    borderTop: "1px solid #c8c8c8",
    borderLeft: "1px solid #c8c8c8",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr 1fr",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  audienceItem: {
    borderRight: "1px solid #c8c8c8",
    borderBottom: "1px solid #c8c8c8",
    padding: theme.spacing(2.5),
  },
  audienceTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: theme.spacing(1.2),
  },
  finalCta: {
    margin: theme.spacing(8, 0),
    border: "1px solid #111",
    padding: theme.spacing(7, 3),
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  finalTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.35,
    letterSpacing: "-0.05em",
    marginBottom: theme.spacing(2),
  },
  stripe: {
    position: "absolute",
    right: 20,
    top: 30,
    bottom: 30,
    width: 48,
    background:
      "repeating-linear-gradient(90deg, transparent 0, transparent 7px, #111 8px, #111 9px)",
    opacity: 0.6,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
}));

const COLLECTIONS = {
  flower: {
    slug: "flower_existence",
    metaTitle:
      "COMME des GARÇONS HOMME PLUS 2022SS 花の存在｜Enclopedia Archive Study",
    metaDescription:
      "COMME des GARÇONS HOMME PLUS 2022SS『花の存在』を、素材の対比、色彩の流れ、コレクション全体の構成から読み解くEnclopediaのアーカイブ解説ページです。",
    metaKeywords:
      "COMME des GARCONS HOMME PLUS 2022SS,花の存在,コムデギャルソンオムプリュス,アーカイブファッション,コレクション解説,enclopedia,アンクロペディア",
    canonicalPath: "/archive_collection/comme_des_garcons_homme_plus_2022ss_flower_existence",
    heroImage: "media/collection/archive1.png",
    heroImageAlt:
      "COMME des GARÇONS HOMME PLUS 2022SS 花の存在のアーカイブコレクション画像",
    heroNumber: "01 / FLOWER",
    label: "ARCHIVE COLLECTION STUDY",
    title: "COMME des GARÇONS HOMME PLUS 2022SS『花の存在』",
    lead:
      "花を幸福の記号として消費するのではなく、美しさと儚さ、癒やしと痛みを同時に引き受ける存在として捉え直したコレクション。\nEnclopediaでは、花柄という分かりやすい視覚の奥にある素材構成とランウェイ全体の流れを読み解きます。",
    keywordLine: ["flower", "contrast", "military", "full bloom"],
    thesis:
      "このコレクションの核は、花の美しさと儚さを、素材・色彩・ショー全体の構成によって同時に見せた点にあります。",
    whyTitle: "花は、なぜここまで強い存在として現れたのか。",
    whyBody:
      "2022SSのHOMME PLUSでは、花は単なる装飾ではありません。異素材とぶつかり、無骨な服の構造へ入り込み、ショー全体の色彩変化の中で徐々に咲き切っていく。\nその見せ方を追うことで、『花の存在』というテーマが服の上でどう成立していたかが見えてきます。",
    pillars: [
      {
        number: "01",
        title: "FLOWER AS PRESENCE",
        text:
          "花を“飾り”として置くのではなく、服全体の印象を変える存在として扱う。花柄そのものがコレクションの主題へ押し上げられています。",
      },
      {
        number: "02",
        title: "MATERIAL CONTRAST",
        text:
          "白黒の花柄生地を黒いフェイクレザーに重ねることで、柔らかさと硬さが衝突する。花の繊細さは、その緊張の中でより鮮明になります。",
      },
      {
        number: "03",
        title: "RUNWAY AS BLOOM",
        text:
          "モノトーンからピンクを含む色彩へ。ショー全体が、花が咲き、満開へ向かう過程のように構成されているようにも見えます。",
      },
    ],
    readingTitle: "ルックとディテールから読む、花の美しさと儚さ。",
    readingIntro:
      "花の存在を理解するうえで重要なのは、“柄がある”ことではなく、“どこに、何と合わせて、どの順序で現れるか”を見ることです。",
    observations: [
      {
        index: "01",
        title: "モノトーンの花柄 × 黒いフェイクレザー",
        text:
          "柔らかな有機性を持つ花柄と、人工的で硬質なレザー感を並置することで、花は守られるのではなく、緊張の中で浮かび上がります。美しさが強まるほど、同時に壊れやすさも見えてくる構成です。",
      },
      {
        index: "02",
        title: "カーキのミリタリーを花柄で切り替える",
        text:
          "無骨さや機能性を担うミリタリーウェアに花柄を差し込むことで、花は弱い装飾ではなく、強い服の意味を変えてしまう存在になります。",
      },
      {
        index: "03",
        title: "モノトーンからピンクへ向かう色の流れ",
        text:
          "序盤は抑制された色から始まり、終盤にかけて花を象徴する色彩が強まっていく。満開は最も美しい瞬間であると同時に、終わりに最も近い瞬間でもある。その二重性がテーマと響き合います。",
      },
    ],
    quote:
      "花は、明るい場面だけのための記号ではない。だからこそこのコレクションでは、華やかさと儚さが同じ強度で立ち上がる。",
    perspectiveTitle: "Enclopediaは、このコレクションを“知識”と“体験”の両方から開く。",
    perspectiveCards: [
      {
        number: "01",
        title: "KNOWLEDGE",
        text:
          "なぜ花なのか。なぜフェイクレザーやミリタリーとぶつけるのか。単なる商品説明ではなく、テーマと服の関係まで可視化します。",
      },
      {
        number: "02",
        title: "EXPERIENCE",
        text:
          "柄の密度、素材の厚み、身体に乗せたときの緊張感は、画像だけでは掴みきれません。レンタルをコレクション体験として設計します。",
      },
      {
        number: "03",
        title: "COMMUNITY",
        text:
          "ギャルソンを詳しく知らなかった人も、花という入口から読み始められる。愛好家・学生・スタイリストが共有できる文脈を育てます。",
      },
    ],
    experienceTitle: "画像で見るだけでは終わらない。実物で、テーマを確かめる。",
    experienceBody:
      "Enclopediaでは、アーカイブを“所有前の試着”に閉じず、服を理解するための体験として扱います。\n花柄の密度、異素材の重さ、着用時の視線の動きまで含めて、このコレクションの魅力に触れられる導線を作ります。",
    experienceItems: [
      "花柄の密度と配置を実物で見る",
      "フェイクレザーとの素材差を触れて理解する",
      "コレクション背景を知ったうえでレンタルする",
    ],
    archiveItemsTitle: "このテーマを、実物で確かめる。",
    archiveItemsBody:
      "在庫導線には、単なる商品カードではなく、“このアイテムのどこを見るべきか”を添える想定です。知識の続きとして商品ページへ進める構成にします。",
    archiveItems: [
      {
        label: "ARCHIVE FOCUS 01",
        title: "Flower × Leather Contrast",
        productPath: "/product/34df6c1d-1b52-45c4-afc3-070b5a46c965",
        productImage:
          "media/product/1760259569.56001334df6c1d-1b52-45c4-afc3-070b5a46c965.jpg",
        text:
          "花柄と黒い質感の衝突を見るための導線。柔らかさが硬質さによって強調される構成を体験できます。",
      },
      {
        label: "ARCHIVE FOCUS 02",
        title: "Military × Floral Reconstruction",
        productPath: "/product/f9dc5b09-8bb5-4828-8d52-ffc553a96ace",
        productImage:
          "media/product/1730183983.8211675f9dc5b09-8bb5-4828-8d52-ffc553a96ace.jpg",
        text:
          "無骨な服の骨格へ花が入り込むことで、印象がどう変わるかを読み解く導線。",
      },
      {
        label: "ARCHIVE FOCUS 03",
        title: "Pink as Full Bloom",
        productPath: "/product/86db861c-951b-47ef-bf45-fc7c33fb2524",
        productImage:
          "media/product/1768918935.165018386db861c-951b-47ef-bf45-fc7c33fb2524.jpg",
        text:
          "ショー終盤の“開花のピーク”を想起させる、色彩と存在感に着目した導線。",
      },
    ],
  },
  orlando: {
    slug: "orlando",
    metaTitle:
      "COMME des GARÇONS HOMME PLUS 2020SS Orlando｜Enclopedia Archive Study",
    metaDescription:
      "COMME des GARÇONS HOMME PLUS 2020SS『Orlando』を、文学・時間・ジェンダーの視点から読み解くEnclopediaのアーカイブ解説ページです。",
    metaKeywords:
      "COMME des GARCONS HOMME PLUS 2020SS,Orlando,オーランドー,コムデギャルソンオムプリュス,アーカイブファッション,コレクション解説,enclopedia,アンクロペディア",
    canonicalPath: "/archive_collection/comme_des_garcons_homme_plus_2020ss_orlando",
    heroImage: "media/collection/archive2.png",
    heroImageAlt:
      "COMME des GARÇONS HOMME PLUS 2020SS Orlandoのアーカイブコレクション画像",
    heroNumber: "02 / ORLANDO",
    label: "ARCHIVE COLLECTION STUDY",
    title: "COMME des GARÇONS HOMME PLUS 2020SS『Orlando』",
    lead:
      "ヴァージニア・ウルフ『オーランドー』を起点に、時代・性・衣服の境界を横断したコレクション。\n古典的な服の形式を借りながら、それを固定された記号ではなく、変化し続ける身体のための装置として読み替えます。",
    keywordLine: ["literature", "transformation", "gender", "time"],
    thesis:
      "『Orlando』の重要性は、過去の様式を引用しながら、“男性服”“女性服”という見え方そのものを流動化した点にあります。",
    whyTitle: "服は、誰のものか。『Orlando』はその問いを曖昧にする。",
    whyBody:
      "2020SSのHOMME PLUSは、文学作品の世界観を単に引用するのではなく、服の形そのものへ翻訳しました。\n時代を越える身体、性別を横断する存在、固定されない自己。その感覚が、テーラリング、装飾、レイヤーの中に現れます。",
    pillars: [
      {
        number: "01",
        title: "LITERATURE INTO CLOTHES",
        text:
          "『オーランドー』の持つ時間の長さと変身の物語を、服のシルエットと重なりによって可視化する。",
      },
      {
        number: "02",
        title: "HISTORY IN COLLISION",
        text:
          "フロックコート、ドレス的な量感、現代的な軽さを同居させ、歴史衣装の再現ではなく、時代の衝突を作る。",
      },
      {
        number: "03",
        title: "BEYOND FIXED GENDER",
        text:
          "男性服と女性服を分けるコードをあえて揺らし、着る人の身体が一つの答えへ固定されない余白を残します。",
      },
    ],
    readingTitle: "ルックとディテールから読む、変化する身体のための服。",
    readingIntro:
      "『Orlando』は、装飾が多いから面白いのではありません。どの装飾が、どの歴史や身体感覚を横断しているかを見ることで、深度が変わります。",
    observations: [
      {
        index: "01",
        title: "古典的なテーラリングに、ドレス的な量感を接続",
        text:
          "肩や上半身には構築性が残る一方で、裾やレイヤーは流動的に広がる。男性服の秩序を保ちながら、その輪郭を別の身体へ開いています。",
      },
      {
        index: "02",
        title: "フリルや装飾を“役割”から自由にする",
        text:
          "装飾は性別を示す記号ではなく、時間の混線や視線の移動を作る要素として働く。飾りが分類を越えるための手段になります。",
      },
      {
        index: "03",
        title: "過去の様式と現代性を同時に置く",
        text:
          "歴史的なフォルムに、現代的な軽さやスピード感を重ねることで、物語の中で時間を越える主人公の感覚が服の上に立ち上がります。",
      },
    ],
    quote:
      "『Orlando』は、服を“誰のものか”で分けるのではなく、“どう変化できるか”で読み直すための提案です。",
    perspectiveTitle: "Enclopediaは、文学と服の間にある距離を、実物体験で縮める。",
    perspectiveCards: [
      {
        number: "01",
        title: "KNOWLEDGE",
        text:
          "文学、ショー、ブランドの意図をつなぎ、なぜ『Orlando』がHOMME PLUSで扱われたのかを可視化します。",
      },
      {
        number: "02",
        title: "EXPERIENCE",
        text:
          "着用すると、丈感、重心、レイヤーの揺れ方が、写真とは違う理解を生みます。服を考える体験へ変えます。",
      },
      {
        number: "03",
        title: "COMMUNITY",
        text:
          "ジェンダー、文学、歴史衣装、舞台芸術に関心のある人が自然に集まる文脈を作り、アーカイブへの入口を広げます。",
      },
    ],
    experienceTitle: "『Orlando』は、読むだけではなく、着ることで輪郭が変わる。",
    experienceBody:
      "画面上では装飾やシルエットとして見える差異が、実際に身体へ乗ると、丈感、重心、視線の集まり方として理解できます。\nEnclopediaでは、アーカイブを所有物ではなく、考え方を身体で辿るメディアとして扱います。",
    experienceItems: [
      "シルエットの重心を身体で知る",
      "装飾とテーラリングの共存を体感する",
      "背景を読んだ後にレンタルで確かめる",
    ],
    archiveItemsTitle: "このテーマを、実物で確かめる。",
    archiveItemsBody:
      "在庫導線には、Orlandoの世界観と接続できる視点を添え、単なる一覧ではなく、コレクション理解の続きを設計します。",
    archiveItems: [
      {
        label: "ARCHIVE FOCUS 01",
        title: "Tailoring Beyond Gender",
        productPath: "/product/8bf5cd8f-d952-4a04-b1c3-34fc46c6c2d5",
        productImage:
          "media/product/1727838682.84216768bf5cd8f-d952-4a04-b1c3-34fc46c6c2d5.png",
        text:
          "男性服の構築性を保ちながら、別の身体感覚へ開いていく一着を読む導線。",
      },
      {
        label: "ARCHIVE FOCUS 02",
        title: "Historical Form / Modern Energy",
        productPath: "/product/0f19e891-2910-4f0a-803d-a593a175ffbf",
        productImage:
          "media/product/1739200273.155390f19e891-2910-4f0a-803d-a593a175ffbf.jpg",
        text:
          "歴史的なフォルムと現在の軽さを同時に扱う、Orlandoらしい混線を示す導線。",
      },
      {
        label: "ARCHIVE FOCUS 03",
        title: "Dressing as Transformation",
        productPath: "/product/034d9b02-fe70-48fc-87af-808c691f88be",
        productImage:
          "media/product/1727830116.7223113034d9b02-fe70-48fc-87af-808c691f88be.png",
        text:
          "着ることで意味が変わる。体験の入口としてレンタルへつなぐ導線。",
      },
    ],
  },
};

const audiences = [
  ["愛好家", "ブランドの背景まで深く知りたい人"],
  ["スタイリスト", "作品づくりのために実物を探す人"],
  ["コレクター", "所有と体験の両方から服を見る人"],
  ["学生", "モードを学ぶ入口を探している人"],
  ["海外層", "日本のアーカイブ視点に触れたい人"],
];

const buildImageUrl = (path) => `${process.env.REACT_APP_IMAGE_URL || ""}${path}`;

function CollectionSeo({ collection }) {
  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}${collection.canonicalPath}`;
  const ogImage = collection.heroImage
    ? buildImageUrl(collection.heroImage)
    : `${process.env.PUBLIC_URL || ""}/favicon.ico`;

  useEffect(() => {
    headRewrite(collection.metaTitle, collection.metaDescription, collection.metaKeywords);
  }, [collection.metaTitle, collection.metaDescription, collection.metaKeywords]);

  return (
    <Helmet>
      <title>{collection.metaTitle}</title>
      <meta name="description" content={collection.metaDescription} />
      <meta name="keywords" content={collection.metaKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={collection.metaTitle} />
      <meta property="og:description" content={collection.metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: collection.metaTitle,
          description: collection.metaDescription,
          mainEntityOfPage: canonicalUrl,
          publisher: {
            "@type": "Organization",
            name: "Enclopedia",
          },
          about: [
            "アーカイブファッション",
            "COMME des GARÇONS HOMME PLUS",
            collection.title,
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: typeof window !== "undefined" ? window.location.origin : "",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Archive Collection",
              item: `${typeof window !== "undefined" ? window.location.origin : ""}/archive_collection`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: collection.title,
              item: canonicalUrl,
            },
          ],
        })}
      </script>
    </Helmet>
  );
}

function ArchiveFocusCard({ item, classes }) {
  const content = (
    <div>
      {item.productImage && (
        <img
          src={buildImageUrl(item.productImage)}
          alt={item.title}
          className={classes.imagePanelImage}
        />
      )}
      <div className={classes.imagePanelLabel}>{item.label}</div>
      <Typography component="h3" className={classes.imagePanelTitle}>
        {item.title}
      </Typography>
      <Typography component="p" className={classes.imagePanelText}>
        {item.text}
      </Typography>
    </div>
  );

  if (item.productPath) {
    return (
      <a
        href={item.productPath}
        className={`${classes.imagePanel} ${classes.imagePanelLink}`}
        aria-label={`${item.title}の商品ページを見る`}
      >
        {content}
      </a>
    );
  }

  return <div className={classes.imagePanel}>{content}</div>;
}

function CollectionArchivePage({ collection }) {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <CollectionSeo collection={collection} />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.hero}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <div className={classes.labelRow}>
                  <span>{collection.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography variant="h1" component="h1" className={classes.heroTitle}>
                  {collection.title}
                </Typography>
                <Typography component="p" className={classes.heroBody}>
                  {collection.lead}
                </Typography>
                <div className={classes.keywordLine}>
                  {collection.keywordLine.map((keyword, index) => (
                    <React.Fragment key={keyword}>
                      <span>{keyword}</span>
                      {index < collection.keywordLine.length - 1 && <span>/</span>}
                    </React.Fragment>
                  ))}
                </div>
                <div className={classes.buttonRow} style={{ marginTop: 32 }}>
                  <Button className={classes.primaryButton} component="a" href="https://www.enclopedia-official.com/">
                    レンタルできるアイテムを見る
                  </Button>
                  <a href="/help_guide" className={classes.textLink}>
                    Enclopediaの使い方
                  </a>
                </div>
              </Grid>
              <Grid item xs={12} md={5}>
                <div className={classes.heroVisual}>
                  <div className={classes.heroCircle} />
                  <div className={classes.heroSquare} />
                  <div className={classes.heroNumber}>{collection.heroNumber}</div>
                  <div className={classes.imageMock}>
                    <img
                      src={buildImageUrl(collection.heroImage)}
                      alt={collection.heroImageAlt}
                      className={classes.imageMockImage}
                    />
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>02 / WHY IT MATTERS</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {collection.whyTitle}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {collection.whyBody}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.thesisCard}>
                  <div>
                    <div className={classes.thesisLabel}>KEY THESIS</div>
                    <Typography component="p" className={classes.thesisText}>
                      {collection.thesis}
                    </Typography>
                  </div>
                  <div className={classes.keywordLine}>
                    <span>knowledge</span>
                    <span>/</span>
                    <span>collection</span>
                    <span>/</span>
                    <span>experience</span>
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>03 / THREE POINTS</span>
              <span className={classes.labelLine} />
            </div>
            <Grid container spacing={3}>
              {collection.pillars.map((pillar) => (
                <Grid item xs={12} md={4} key={pillar.number}>
                  <div className={classes.card}>
                    <div>
                      <div className={classes.cardNumber}>{pillar.number}</div>
                      <div className={classes.cardTitle}>{pillar.title}</div>
                      <div className={classes.smallRule} />
                      <Typography component="p" className={classes.body}>
                        {pillar.text}
                      </Typography>
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>04 / HOW TO READ</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {collection.readingTitle}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {collection.readingIntro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.archiveList}>
                  {collection.observations.map((item) => (
                    <div className={classes.archiveListItem} key={item.index}>
                      <div className={classes.archiveListIndex}>{item.index}</div>
                      <div className={classes.archiveListTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.quoteBlock}>
              <div className={classes.quoteMark}>“</div>
              <Typography component="p" className={classes.quoteText}>
                {collection.quote}
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>05 / ENCLOPEDIA PERSPECTIVE</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {collection.perspectiveTitle}
            </Typography>
            <Grid container spacing={3}>
              {collection.perspectiveCards.map((card) => (
                <Grid item xs={12} md={4} key={card.number}>
                  <div className={classes.card}>
                    <div>
                      <div className={classes.cardNumber}>{card.number}</div>
                      <div className={classes.cardTitle}>{card.title}</div>
                      <div className={classes.smallRule} />
                      <Typography component="p" className={classes.body}>
                        {card.text}
                      </Typography>
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>06 / EXPERIENCE</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {collection.experienceTitle}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {collection.experienceBody}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.experienceBox}>
                  {collection.experienceItems.map((item) => (
                    <div className={classes.experienceItem} key={item}>
                      <span>{item}</span>
                      <span className={classes.experienceArrow}>→</span>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>07 / ARCHIVE FOCUS</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {collection.archiveItemsTitle}
            </Typography>
            <Typography component="p" className={classes.heroBody}>
              {collection.archiveItemsBody}
            </Typography>
            <div className={classes.imageGrid}>
              {collection.archiveItems.map((item) => (
                <ArchiveFocusCard item={item} classes={classes} key={item.label} />
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>08 / AUDIENCE</span>
              <span className={classes.labelLine} />
            </div>
            <div className={classes.audienceGrid}>
              {audiences.map(([title, text]) => (
                <div className={classes.audienceItem} key={title}>
                  <div className={classes.audienceTitle}>{title}</div>
                  <Typography component="p" className={classes.noteBody}>
                    {text}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <div className={classes.finalCta}>
            <div className={classes.stripe} />
            <Typography component="p" className={classes.finalTitle}>
              アーカイブを、読むだけで終わらせない。
            </Typography>
            <Typography component="p" className={classes.heroBody} style={{ margin: "0 auto 32px" }}>
              コレクションの背景を知り、実物に触れ、服を体験として理解するための入口です。
            </Typography>
            <Button className={classes.primaryButton} component="a" href="/">
              アイテムを探す
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}

function ArchiveCollectionIndex() {
  const classes = useStyles();
  const collections = useMemo(() => Object.values(COLLECTIONS), []);

  useEffect(() => {
    headRewrite(
      "Archive Collection Study｜Enclopedia",
      "Enclopediaが扱うブランドアーカイブの各コレクション解説一覧ページです。",
      "archive collection,enclopedia,アーカイブファッション,コレクション解説"
    );
  }, []);

  return (
    <div className={classes.root}>
      <Helmet>
        <title>Archive Collection Study｜Enclopedia</title>
        <meta
          name="description"
          content="Enclopediaが扱うブランドアーカイブの各コレクション解説一覧ページです。"
        />
        <link
          rel="canonical"
          href={`${typeof window !== "undefined" ? window.location.origin : ""}/archive_collection`}
        />
      </Helmet>
      <Container maxWidth="xl">
        <section className={classes.hero}>
          <div className={classes.labelRow}>
            <span>ARCHIVE COLLECTION STUDY</span>
            <span className={classes.labelLine} />
          </div>
          <Typography variant="h1" component="h1" className={classes.heroTitle}>
            コレクション解説
          </Typography>
          <Typography component="p" className={classes.heroBody}>
            ブランドの各コレクションを、背景・ルック・素材・体験導線から読み解くためのページです。
          </Typography>
        </section>
        <section className={classes.section}>
          <div className={classes.archiveList}>
            {collections.map((collection, index) => (
              <a
                href={collection.canonicalPath}
                className={classes.archiveListItem}
                key={collection.canonicalPath}
                style={{ color: "inherit", textDecoration: "none" }}
              >
                <div className={classes.archiveListIndex}>
                  {String(index + 1).padStart(2, "0")}
                </div>
                <div className={classes.archiveListTitle}>{collection.title}</div>
                <Typography component="p" className={classes.noteBody}>
                  {collection.metaDescription}
                </Typography>
              </a>
            ))}
          </div>
        </section>
      </Container>
    </div>
  );
}

const findCollectionBySlug = (slug) => {
  if (!slug) return null;
  return Object.values(COLLECTIONS).find((collection) => {
    const canonicalSlug = collection.canonicalPath.split("/").filter(Boolean).pop();
    return collection.slug === slug || canonicalSlug === slug;
  });
};

export default function ArchiveCollectionScreen() {
  const { slug } = useParams();
  const collection = useMemo(() => findCollectionBySlug(slug), [slug]);

  if (!slug) {
    return <ArchiveCollectionIndex />;
  }

  if (!collection) {
    return (
      <div style={{ padding: 48 }}>
        <Helmet>
          <title>Archive Collection Not Found｜Enclopedia</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Typography component="h1" variant="h1">
          Archive collection not found
        </Typography>
      </div>
    );
  }

  return <CollectionArchivePage collection={collection} />;
}
